import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import { BombiixCard } from '../BombiixCard';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewToggle } from '../../PIM/common/NewToggle';
import {
  EditIcon,
  DeleteIcon,
  ApproveIcon,
  RejectIcon,
  ResendPasswordIcon,
  MoreIcon,
  InfoIcon,
  FactoryIcon,
  SupportingDocumentsIcon,
  PartnersIcon,
  SetPasswordIcon,
  TwoFAIcon,
  ChevronIcon,
} from '../../../bombiix-icon';
import _BtnClock from '../../Utility/FilterAction/BtnClock';
import { recoverUsersMFA as _recoverUsersMFA } from '../../../Pages/Auth/store/Auth.action';
import {
  sendFactoryUpdateLink as _sendFactoryUpdateLink,
  updateSupplierActiveStateAction as _updateSupplierActiveStateAction,
} from '../../../Pages/Supplier/store/Supplier.action';
import _SetNewPassword from '../../../Pages/UserSetting/UsersLibrary/SetNewPasswordModal';
import { toasterMsg } from '../../Toaster/Toaster';
import noImage from '../../../assets/images/no-image.png';
import _CustomTooltip from '../../Tooltip/CustomTooltip';
import BombiixModal from '../../BombiixModal/BombiixModal';
import { BombiixChip } from '../../BombiixChip';
import { AppDispatch, useAppDispatch } from '../../../hooks/useAppDispatch';

type Thunk<TResult> = (dispatch: AppDispatch) => Promise<TResult>;

/**
 * The factory-link schedule stored for a supplier.
 */
interface SupplierFactoryLink {
  intervalTime?: string;
  endDate?: string;
  updatedAt?: string;
}

/**
 * The supplier fields the overview card reads.
 */
export interface SupplierOverviewItem {
  _id?: string;
  code?: string;
  supplierName?: string;
  email?: string;
  logo?: string;
  supplierType?: Array<string | number>;
  suppliedFromNames?: string[];
  buildingNo?: string;
  streetAddress?: string;
  townCity?: string;
  zipCode?: string;
  country?: string;
  accountVerified?: boolean;
  canSuggest?: boolean;
  autoApproved?: boolean;
  isActive?: boolean;
  factoryUpdateDate?: string;
  factoryLink?: SupplierFactoryLink | null;
}

interface ProductComponentOption {
  value: string | number;
  label?: string;
}

interface FactoryLinkRequest {
  supplier: Array<string | undefined>;
  intervalTime: string | undefined;
  loggedInUserId: string | undefined;
  endDate: string | null;
}

interface CustomTooltipProps {
  tooltipText: React.ReactNode;
  placement?: string;
  children?: React.ReactNode;
}

interface BtnClockProps {
  iconClass: string;
  item: SupplierOverviewItem;
  onIntervalChange: (option: { value: string }) => void;
  intervalTime: string | undefined;
  onSubmitRequest: () => void;
  btnClass: string;
  loading: boolean;
  dropdownUp: boolean;
}

interface SetNewPasswordProps {
  title: string;
  user: SupplierOverviewItem;
  show: boolean;
  toggle: (user: SupplierOverviewItem) => void;
}

interface SupplierOverviewCardState {
  UserDetails: { userInfo?: { userId?: string } };
}

interface SupplierOverviewCardProps {
  item: SupplierOverviewItem;
  index: number;
  onDelete: (supplierId: string | undefined) => void;
  productComponentCollection: ProductComponentOption[] | undefined;
  onSuggestSwitchChange: (event: React.ChangeEvent<HTMLInputElement>, supplierId: string | undefined, field: 'supplierSuggest' | 'autoApproved') => void;
  integrateSupplier: (supplier: { id: string | undefined; email: string | undefined }) => void;
  mailIconStatus: unknown;
  CostingAddEditAccess: boolean;
  CostingDeleteAccess: boolean;
  fetchSuppliersList: () => void;
  selectedIds: Array<string | undefined> | undefined;
  onCheck: (event: React.ChangeEvent<HTMLInputElement>, supplierId: string | undefined) => void;
  refreshPage: () => void;
  selectedTier: string;
}

const CustomTooltip = _CustomTooltip as unknown as React.FC<CustomTooltipProps>;
const BtnClock = _BtnClock as unknown as React.FC<BtnClockProps>;
const SetNewPassword = _SetNewPassword as unknown as React.FC<SetNewPasswordProps>;
const recoverUsersMFA = _recoverUsersMFA as unknown as (
  data: { email: string | undefined; userId: string | undefined },
  trans: TFunction<'common'>,
) => Thunk<unknown>;
const sendFactoryUpdateLink = _sendFactoryUpdateLink as unknown as (dataObj: FactoryLinkRequest, transactionMsg: TFunction<'common'>) => Thunk<unknown>;
const updateSupplierActiveStateAction = _updateSupplierActiveStateAction as unknown as (
  id: string | undefined,
  isActive: boolean,
  transactionMsg: TFunction<'modules/partner-overview'>,
) => Thunk<boolean>;

/** Gap between type chips (`.partner-card-type-chips` uses `gap: 2px`). */
const TYPE_CHIP_GAP = 2;

/** Width reserved for the overflow marker chip, so it never runs past the cell. */
const TYPE_MORE_WIDTH = 28;

/**
 * Recurring periods that carry a next-update date. The picker supplies its values
 * as strings, so these are compared as strings — a strict comparison against the
 * numbers 3/6/9 never matched, which left `endDate` null on every card-level
 * request and pinned the status icon to its grey, never-scheduled state.
 */
const MONTHLY_INTERVALS = ['3', '6', '9'];

/**
 * One supplier card on the Partner Overview listing.
 *
 * Cards open collapsed: one row of types, expanded on demand. The chips always
 * render; collapsing clips them to a single row rather than hiding them, so a card
 * still says what the partner supplies without being expanded, and every card
 * keeps the same height whatever its number of types. The toggle only shows when
 * the types run past one row.
 *
 * The interval picker is seeded from the schedule already stored for this
 * supplier, and re-synced after a save so the panel reopens on the period that is
 * actually set rather than an empty selection.
 *
 * The active-state confirmation uses the modal's own action bar rather than a
 * button hand-placed in the body, so it has the same shape as every other
 * confirmation.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The card.
 */
function SupplierOverviewCard({
  item,
  index,
  onDelete,
  productComponentCollection,
  onSuggestSwitchChange,
  integrateSupplier,
  mailIconStatus,
  CostingAddEditAccess,
  CostingDeleteAccess,
  fetchSuppliersList,
  selectedIds,
  onCheck,
  refreshPage,
  selectedTier,
}: SupplierOverviewCardProps) {
  const dispatch = useAppDispatch();
  const reducer = useSelector((reducerstate: SupplierOverviewCardState) => reducerstate);

  const { t } = useTranslation('common');
  const { t: tPartnerOverview } = useTranslation('modules/partner-overview');
  const { UserDetails } = reducer;
  const { userInfo } = UserDetails;
  const history = useHistory();

  const [isTypeListOpen, setIsTypeListOpen] = useState(false);
  const typeChipsRef = useRef<HTMLSpanElement | null>(null);
  const [isTypeListMultiline, setIsTypeListMultiline] = useState(false);
  const [typeMoreOffset, setTypeMoreOffset] = useState(0);
  const [logoSrc, setLogoSrc] = useState<string>(noImage);
  const [showChangePassModel, setShowChangePassModel] = useState(false);
  const [user, setUser] = useState<SupplierOverviewItem>({});
  const [intervalTime, setIntervalTime] = useState(item?.factoryLink?.intervalTime);

  useEffect(() => {
    setIntervalTime(item?.factoryLink?.intervalTime);
  }, [item?.factoryLink?.intervalTime]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showActiveStateConfirm, setShowActiveStateConfirm] = useState(false);
  const [activeStateSaving, setActiveStateSaving] = useState(false);
  const isChecked = (selectedIds?.filter((val) => val === item?._id).length ?? 0) > 0;
  const isProductionLocationTier = selectedTier !== 'Tier1';

  const recoverMfa = async (id: string | undefined, email: string | undefined) => {
    const data1 = {
      email,
      userId: id,
    };
    const res = await dispatch(recoverUsersMFA(data1, t));
    if (res) fetchSuppliersList();
  };
  const toggleChangePasswordModel = (ele: SupplierOverviewItem) => {
    setShowChangePassModel((prev) => !prev);
    setUser(ele);
  };
  const handleEdit = (supplier: SupplierOverviewItem) => {
    history.push(`/suppliers/edit/${supplier._id}`);
  };
  const onIntervalChange = (e: { value: string }) => {
    setIntervalTime(e.value);
  };

  const onSubmitRequest = async () => {
    const currentDate = moment();
    let futureDate;
    if (!intervalTime) {
      toasterMsg(t('COMMON.PLEASE_SELECT_RECURRING_PERIOD'), 'danger');
    } else {
      if (MONTHLY_INTERVALS.includes(String(intervalTime))) {
        futureDate = currentDate.add(Number(intervalTime), 'months');
      }
      const dataObj = {
        supplier: [item?._id],
        intervalTime,
        loggedInUserId: userInfo?.userId,
        endDate: futureDate ? futureDate.toISOString() : null,
      };
      setLoading(true);
      const res = await dispatch(sendFactoryUpdateLink(dataObj, t));
      setLoading(false);
      if (res) {
        refreshPage();
      }
    }
  };

  /**
   * Status colour for the factory-update clock: amber when an update is due within a
   * week, red once the update time has passed, green when the factory was updated
   * inside the current interval, grey otherwise.
   * @returns {string} The icon colour class.
   */
  const getClassName = () => {
    const currentDate = moment();
    const factoryUpdateDate = item?.factoryUpdateDate ? moment(item?.factoryUpdateDate) : null;
    const nextIntervalDate = item?.factoryLink?.endDate ? moment(item?.factoryLink?.endDate) : null;

    if (item?.factoryLink?.intervalTime === 'instant' && !factoryUpdateDate) {
      return 'gray';
    }

    if (nextIntervalDate && item?.factoryLink?.intervalTime !== 'instant') {
      const daysDifference = nextIntervalDate.diff(currentDate, 'days');

      if (daysDifference >= 1 && daysDifference <= 7) {
        return 'orange';
      }

      if (nextIntervalDate.isBefore(currentDate)) {
        return 'red';
      }

      if (factoryUpdateDate?.isBefore(nextIntervalDate)) {
        return 'green';
      }
    }

    return 'gray';
  };

  const isSupplierActive = item?.isActive !== false;

  /**
   * Applies the pending availability change. An inactive supplier keeps every product
   * that already references it and still answers where-used and filter queries; it is
   * only withheld from the pickers used to build a new product.
   */
  const confirmActiveStateChange = async () => {
    setActiveStateSaving(true);
    const res = await dispatch(updateSupplierActiveStateAction(item?._id, !isSupplierActive, tPartnerOverview));
    setActiveStateSaving(false);
    setShowActiveStateConfirm(false);
    if (res) {
      refreshPage();
    }
  };

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow);
  };

  const handleNavigate = (path: string) => {
    history.push(path);
  };

  /**
   * Routes the card title through the router instead of letting the anchor do a
   * full page load. `BombiixCard.Title` renders a real `<a>` so the destination
   * still shows in the status bar and opens in a new tab on middle-click; only
   * the plain left-click is intercepted.
   * @param {React.MouseEvent<HTMLAnchorElement>} event - The click.
   * @returns {void}
   */
  const handleTitleNavigate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    handleNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  useEffect(() => {
    const candidate = item?.logo;
    if (!candidate) {
      setLogoSrc(noImage);
      return undefined;
    }
    let active = true;
    const probe = new Image();
    probe.onload = () => {
      if (active) setLogoSrc(candidate);
    };
    probe.onerror = () => {
      if (active) setLogoSrc(noImage);
    };
    probe.src = candidate;
    return () => {
      active = false;
    };
  }, [item?.logo]);

  const productTypeNames =
    productComponentCollection
      ?.filter((product) => item?.supplierType?.includes(product?.value))
      .map((prod) => prod?.label)
      .filter(Boolean) || [];

  /**
   * Whether the types run to more than one row. It cannot be derived from the
   * count — how many fit depends on the card's width and each name's length —
   * so it is measured, and re-measured when the card resizes.
   *
   * The test is the content's natural height against one chip's height, not
   * `scrollHeight > clientHeight`: the latter is only true while the row is
   * clipped, so expanding would report "fits on one line" and take the toggle
   * away, stranding the card open. The overflow marker sits just past the last
   * chip on the first row, so it reads as the next badge in the run; chips on
   * later rows have a greater `offsetTop`.
   */
  useLayoutEffect(() => {
    const element = typeChipsRef.current;
    if (!element) return undefined;
    const measureRows = () => {
      const firstChip = element.firstElementChild instanceof HTMLElement ? element.firstElementChild : null;
      const rowHeight = firstChip ? firstChip.offsetHeight : 0;
      const multiline = rowHeight > 0 && element.scrollHeight > rowHeight + 1;
      setIsTypeListMultiline(multiline);
      if (!multiline || !firstChip) return;
      const containerLeft = element.getBoundingClientRect().left;
      const firstRowTop = firstChip.offsetTop;
      const firstRowChips = Array.from(element.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement && !child.classList.contains('partner-card-type-more') && child.offsetTop === firstRowTop,
      );
      const lastOnRow = firstRowChips[firstRowChips.length - 1];
      if (!lastOnRow) return;
      const offset = lastOnRow.getBoundingClientRect().right - containerLeft + TYPE_CHIP_GAP;
      setTypeMoreOffset(Math.max(0, Math.min(offset, element.clientWidth - TYPE_MORE_WIDTH)));
    };
    measureRows();
    const observer = new ResizeObserver(measureRows);
    observer.observe(element);
    return () => observer.disconnect();
  }, [isTypeListOpen, productTypeNames.length]);

  const fullAddress = item?.streetAddress
    ? `${item?.buildingNo ? `${item?.buildingNo}, ` : ''}${item?.streetAddress}, ${item?.townCity}, ${item?.zipCode}, ${item?.country}`
    : '';

  return (
    <>
      <div className="partner-card-cell">
        <BombiixCard className="partner-card">
          <BombiixCard.Header>
            <BombiixCard.Actions>
              <li>
                <NewCheckbox
                  id={`checkProduct${item?._id}`}
                  name={`checkProduct${item?._id}`}
                  checked={isChecked}
                  aria-label={t('BTN_FILTER.SELECT')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onCheck(e, item?._id);
                  }}
                />
              </li>
              <CustomTooltip placement="top" tooltipText={t('COMMON.EDIT')}>
                <li>
                  <button type="button" className="bombiix-icon-container" aria-label="Edit" onClick={() => handleEdit(item)}>
                    <EditIcon size={24} />
                  </button>
                </li>
              </CustomTooltip>
              {selectedTier === 'Tier1' && (
                <CustomTooltip placement="top" tooltipText={item?.accountVerified ? t('COMMON.INTEGRATED') : t('COMMON.INTEGRATE')}>
                  <li>
                    <button
                      type="button"
                      className={`bombiix-icon-container${item?.accountVerified ? ' bombiix-icon-container--green' : ''}`}
                      aria-label={item?.accountVerified ? t('COMMON.INTEGRATED') : t('COMMON.INTEGRATE')}
                      disabled={!item?.accountVerified && Boolean(mailIconStatus)}
                      onClick={
                        item?.accountVerified
                          ? undefined
                          : () => {
                              integrateSupplier({ id: item?._id, email: item?.email });
                            }
                      }
                    >
                      <ResendPasswordIcon size={24} />
                    </button>
                  </li>
                </CustomTooltip>
              )}
              {CostingAddEditAccess && selectedTier === 'Tier1' && (
                <BtnClock
                  iconClass={getClassName()}
                  item={item}
                  onIntervalChange={onIntervalChange}
                  intervalTime={intervalTime}
                  onSubmitRequest={onSubmitRequest}
                  btnClass="bombiix-icon-container"
                  loading={loading}
                  dropdownUp={false}
                />
              )}
              {CostingDeleteAccess && (
                <CustomTooltip placement="top" tooltipText={t('COMMON.DELETE')}>
                  <li>
                    <button
                      type="button"
                      aria-label="Delete"
                      disabled
                      className="bombiix-icon-container bombiix-icon-container--red"
                      onClick={() => {
                        onDelete(item?._id);
                      }}
                    >
                      <DeleteIcon size={24} />
                    </button>
                  </li>
                </CustomTooltip>
              )}
              {selectedTier !== 'Tier1' && (
                <>
                  <CustomTooltip placement="top" tooltipText={tPartnerOverview('NEW_PARTNER_OVERVIEW.PRODUCTION_lOCATION')}>
                    <li>
                      <button
                        type="button"
                        aria-label={tPartnerOverview('NEW_PARTNER_OVERVIEW.PRODUCTION_lOCATION')}
                        className="bombiix-icon-container"
                        onClick={() => handleNavigate(`/supplier/${item?._id}/component-suppliers?supplierId=${item?._id}`)}
                      >
                        <FactoryIcon size={24} />
                      </button>
                    </li>
                  </CustomTooltip>
                  <CustomTooltip placement="top" tooltipText={t('COMPONENT_SUPPLIER_LISTING.SUPPORTING_DOCUMENT')}>
                    <li>
                      <button
                        type="button"
                        aria-label="Supporting Documents"
                        className="bombiix-icon-container"
                        onClick={() => handleNavigate(`/supplier/${item?._id}/supporting-docs?supplierId=${item?._id}`)}
                      >
                        <SupportingDocumentsIcon size={24} />
                      </button>
                    </li>
                  </CustomTooltip>
                </>
              )}
              {selectedTier === 'Tier1' && (
                <CustomTooltip placement="top" tooltipText={isSupplierActive ? t('SUPLIER.ACTIVE') : t('SUPLIER.INACTIVE')}>
                  <li className="partner-card-actions__right">
                    <button
                      type="button"
                      className={`bombiix-icon-container partner-card-status${isSupplierActive ? ' is-active' : ''}`}
                      aria-label={isSupplierActive ? t('SUPLIER.ACTIVE') : t('SUPLIER.INACTIVE')}
                      aria-pressed={isSupplierActive}
                      disabled={!CostingAddEditAccess}
                      onClick={() => setShowActiveStateConfirm(true)}
                    >
                      {isSupplierActive ? <ApproveIcon size={24} /> : <RejectIcon size={24} />}
                    </button>
                  </li>
                </CustomTooltip>
              )}
              {selectedTier === 'Tier1' && (
                <li className="partner-card-actions__right">
                  <OverlayTrigger
                    placement="bottom"
                    trigger="click"
                    rootClose
                    onToggle={handleToggle}
                    overlay={
                      <Popover id={`supplier-card-actions-${item?._id}`} className="table-action-popup bombiix-action-menu">
                        <Popover.Content>
                          <ul>
                            <li>
                              <button
                                type="button"
                                className="btn d-flex align-items-center"
                                onClick={() => handleNavigate(`/supplier/${item?._id}/users?supplierId=${item?._id}`)}
                              >
                                <PartnersIcon size={24} />
                                <span className="text ml-8">{t('USER_LISTING.USERS')}</span>
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="btn d-flex align-items-center"
                                onClick={() => handleNavigate(`/supplier/${item?._id}/component-suppliers?supplierId=${item?._id}`)}
                              >
                                <FactoryIcon size={24} />
                                <span className="text ml-8">{t('COMPONENT_SUPPLIER_LISTING.FACTORIES')}</span>
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="btn d-flex align-items-center"
                                onClick={() => handleNavigate(`/supplier/${item?._id}/supporting-docs?supplierId=${item?._id}`)}
                              >
                                <SupportingDocumentsIcon size={24} />
                                <span className="text ml-8">{t('COMPONENT_SUPPLIER_LISTING.SUPPORTING_DOCUMENT')}</span>
                              </button>
                            </li>
                            {CostingAddEditAccess && item?.accountVerified && (
                              <>
                                <li>
                                  <button type="button" className="btn d-flex align-items-center" onClick={() => recoverMfa(item?._id, item?.email)}>
                                    <TwoFAIcon size={24} />
                                    <span className="text ml-8">{t('COMMON.RESET_2FA_ACCOUNT')}</span>
                                  </button>
                                </li>
                                <li>
                                  <button type="button" className="btn d-flex align-items-center" onClick={() => toggleChangePasswordModel(item)}>
                                    <SetPasswordIcon size={24} />
                                    <span className="text ml-8">{t('COMMON.SET_PASSWORD')}</span>
                                  </button>
                                </li>
                              </>
                            )}
                          </ul>
                        </Popover.Content>
                      </Popover>
                    }
                  >
                    <button type="button" className={`bombiix-icon-container${isOpen ? ' active' : ''}`} aria-label={t('PARTNER_OVERVIEW.ACTION')}>
                      <MoreIcon size={24} />
                    </button>
                  </OverlayTrigger>
                </li>
              )}
            </BombiixCard.Actions>
            <BombiixCard.Thumb src={logoSrc} alt={item?.supplierName ?? ''} enlargeOnHover />
          </BombiixCard.Header>
          <BombiixCard.Body>
            <BombiixCard.Title
              href={
                selectedTier === 'Tier1'
                  ? `/supplier/${item?._id}/users?supplierId=${item?._id}`
                  : `/supplier/${item?._id}/component-suppliers?supplierId=${item?._id}`
              }
              onClick={handleTitleNavigate}
            >
              <CustomTooltip placement="top" tooltipText={item?.code}>
                <span>{item?.code || ''}</span>
              </CustomTooltip>
            </BombiixCard.Title>
            <BombiixCard.Meta>
              <li>
                <strong>{t('USER_LISTING.NAME')} </strong>
                <CustomTooltip placement="top" tooltipText={item?.supplierName}>
                  <span>{item?.supplierName || ''}</span>
                </CustomTooltip>
              </li>
              <li className="partner-card-type-cell">
                <div className="partner-card-type-head">
                  <strong>{selectedTier === 'Tier1' ? t('USER_LISTING.PRODUCT_TYPE') : t('USER_LISTING.PRODUCT_COMPONENT_TYPE')} </strong>
                  {isTypeListMultiline ? (
                    <button
                      type="button"
                      className="partner-card-type-toggle"
                      aria-expanded={isTypeListOpen}
                      aria-label={t('USER_LISTING.PRODUCT_COMPONENT_TYPE')}
                      onClick={() => setIsTypeListOpen((open) => !open)}
                    >
                      <ChevronIcon size={20} />
                    </button>
                  ) : null}
                </div>
                <span ref={typeChipsRef} className={`partner-card-type-chips${isTypeListOpen ? '' : ' partner-card-type-chips--collapsed'}`}>
                  {productTypeNames.map((name) => (
                    <BombiixChip key={name} variant="pink">
                      {name}
                    </BombiixChip>
                  ))}
                  {!isTypeListOpen && isTypeListMultiline ? (
                    <span className="partner-card-type-more" style={{ left: `${typeMoreOffset}px` }} aria-hidden="true">
                      <BombiixChip variant="pink">…</BombiixChip>
                    </span>
                  ) : null}
                </span>
              </li>
              <li>
                <strong>{t('COMPONENT_SUPPLIER_LISTING.ADDRESS')} </strong>
                <CustomTooltip placement="top" tooltipText={fullAddress}>
                  <span>{fullAddress}</span>
                </CustomTooltip>
              </li>
              {selectedTier === 'Tier1' && (
                <li>
                  <strong>{t('COMPONENT_SUPPLIER_LISTING.EMAIL')} </strong>
                  <CustomTooltip placement="top" tooltipText={item?.email}>
                    <span>{item?.email || ''}</span>
                  </CustomTooltip>
                </li>
              )}
            </BombiixCard.Meta>
          </BombiixCard.Body>
          {!isProductionLocationTier && (
            <BombiixCard.Footer className="partner-card__footer">
              <NewToggle
                id={`supplierSuggestSwitch_${index}`}
                name={`supplierSuggestSwitch_${index}`}
                checked={Boolean(item?.canSuggest)}
                label={t('COMPONENT_SUPPLIER_LISTING.SUPPLIER_SUGGEST')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onSuggestSwitchChange(e, item?._id, 'supplierSuggest');
                }}
              />
              <NewToggle
                id={`autoApprovalSwitch_${index}`}
                name={`autoApprovalSwitch_${index}`}
                checked={Boolean(item?.autoApproved)}
                disabled={!item?.canSuggest}
                label={t('COMPONENT_SUPPLIER_LISTING.AUTO_APPROVED')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onSuggestSwitchChange(e, item?._id, 'autoApproved');
                }}
              />
              <OverlayTrigger
                trigger={['hover', 'focus']}
                rootClose
                overlay={
                  <Popover id={`supplier-card-info-${item?._id}`} className="custom-popover modified-popover">
                    <div className="p-2">{t('COMPONENT_SUPPLIER_LISTING.INFO_MESSAGE')}</div>
                  </Popover>
                }
              >
                <button aria-label="info" type="button" className="bombiix-icon-container ml-auto">
                  <InfoIcon size={24} />
                </button>
              </OverlayTrigger>
            </BombiixCard.Footer>
          )}
        </BombiixCard>
      </div>
      {showChangePassModel && (
        <div className="modal-body">
          <SetNewPassword title={t('COMPONENT_SUPPLIER_LISTING.SET_NEW_PASSWORD')} user={user} show={showChangePassModel} toggle={toggleChangePasswordModel} />
        </div>
      )}
      {showActiveStateConfirm && (
        <BombiixModal
          show={showActiveStateConfirm}
          title={isSupplierActive ? t('SUPLIER.DEACTIVATE_SUPPLIER_TITLE') : t('SUPLIER.ACTIVATE_SUPPLIER_TITLE')}
          onClose={() => setShowActiveStateConfirm(false)}
          onSave={confirmActiveStateChange}
          saveLabel={isSupplierActive ? t('SUPLIER.DEACTIVATE') : t('SUPLIER.ACTIVATE')}
          saveVariant={isSupplierActive ? 'red' : 'blue'}
          saveDisabled={activeStateSaving}
          size="sm"
          className="partner-state-modal"
        >
          <p className="partner-state-modal__message">{isSupplierActive ? t('SUPLIER.DEACTIVATE_SUPPLIER_MESSAGE') : t('SUPLIER.ACTIVATE_SUPPLIER_MESSAGE')}</p>
        </BombiixModal>
      )}
    </>
  );
}

export default SupplierOverviewCard;
